import { useRef, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";

// Image quality presets
export const ImageQuality = {
	ultra: 1,
	high: 0.92,
	medium: 0.8,
	low: 0.6,
	ultraLow: 0.4,
} as const;

// Crop area presets
export const CropAreas = {
	none: { aspectRatio: 0, name: "none" },
	portrait: { aspectRatio: 3 / 4, name: "portrait" },
	square: { aspectRatio: 1, name: "square" },
	selfie: { aspectRatio: 2 / 3, name: "selfie" },
} as const;

export type CropArea = (typeof CropAreas)[keyof typeof CropAreas];

export type UseCameraOptions = {
	quality?: number;
	cropArea?: CropArea;
	maxWidth?: number;
};

export const useCamera = (
	onTerminalError?: () => void,
	options: UseCameraOptions = {},
) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const onTerminalErrorRef = useRef(onTerminalError);

	useEffect(() => {
		onTerminalErrorRef.current = onTerminalError;
	}, [onTerminalError]);

	useEffect(() => {
		let canceled = false;

		const startCamera = async () => {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({
					video: {
						facingMode: "user",
						width: { ideal: 1920, max: 2560 },
						height: { ideal: 1080, max: 1440 },
					},
					audio: false,
				});

				if (canceled) {
					for (const track of stream.getTracks()) track.stop();
					return;
				}

				streamRef.current = stream;
				if (videoRef.current) {
					videoRef.current.srcObject = stream;
				}
			} catch (error: unknown) {
				if (typeof error === "object" && error !== null && "name" in error) {
					const errorName = (error as { name: string }).name;
					const isTerminal =
						errorName === "NotAllowedError" ||
						errorName === "NotFoundError" ||
						errorName === "SecurityError";

					if (isTerminal) {
						onTerminalErrorRef.current?.();
						return;
					}
				}

				toast({
					title: "Camera Access Error",
					description:
						typeof error === "object" && error !== null && "message" in error
							? (error as { message: string }).message
							: "Could not access the camera.",
					variant: "destructive",
				});
			}
		};

		startCamera();

		return () => {
			canceled = true;

			if (streamRef.current) {
				for (const track of streamRef.current.getTracks()) {
					track.stop();
				}
				streamRef.current = null;
			}

			if (videoRef.current) {
				try {
					videoRef.current.pause();
				} catch {}
				videoRef.current.srcObject = null;
			}
		};
	}, []);

	const captureImage = (): Promise<Blob | null> => {
		return new Promise((resolve) => {
			if (!videoRef.current) {
				resolve(null);
				return;
			}

			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			if (!ctx) {
				resolve(null);
				return;
			}

			const originalWidth = videoRef.current.videoWidth;
			const originalHeight = videoRef.current.videoHeight;

			const {
				quality = ImageQuality.high,
				cropArea = CropAreas.none,
				maxWidth = 720,
			} = options;

			let cropWidth: number;
			let cropHeight: number;
			let newWidth: number;
			let newHeight: number;

			if (cropArea.name === "none") {
				cropWidth = originalWidth;
				cropHeight = originalHeight;
			} else {
				const targetAspectRatio = cropArea.aspectRatio;

				// Calculate crop dimensions to get the center portion
				if (originalWidth / originalHeight > targetAspectRatio) {
					// Original is wider than target - crop width
					cropHeight = originalHeight;
					cropWidth = originalHeight * targetAspectRatio;
				} else {
					// Original is taller than target - crop height
					cropWidth = originalWidth;
					cropHeight = originalWidth / targetAspectRatio;
				}
			}

			// Scale down if still too large
			if (cropWidth > maxWidth) {
				const scale = maxWidth / cropWidth;
				newWidth = maxWidth;
				newHeight = cropHeight * scale;
			} else {
				newWidth = cropWidth;
				newHeight = cropHeight;
			}

			canvas.width = newWidth;
			canvas.height = newHeight;

			// Calculate source crop position (center the crop)
			const sourceX = (originalWidth - cropWidth) / 2;
			const sourceY = (originalHeight - cropHeight) / 2;

			// Draw the cropped and scaled portion
			ctx.drawImage(
				videoRef.current,
				sourceX,
				sourceY,
				cropWidth,
				cropHeight,
				0,
				0,
				newWidth,
				newHeight,
			);

			canvas.toBlob(resolve, "image/jpeg", quality);
		});
	};

	return {
		videoRef,
		captureImage,
	};
};
